import * as React from 'react';
import type { ConnectedProps } from 'react-redux';
import { connect } from 'react-redux';
import { compose } from 'redux';

import type { FieldReportFullViewFilterContextType } from 'af-root/context/fieldReportFullViewFilterContext';

import AdditionalFilter from 'af-components/Table/Filters/AdditionalFilter/Multiselect';

import * as AccountActions from 'af-actions/accounts';

export interface SuperintendentFilterOption {
	id: number;
	label: string;
}

interface OwnProps {
	onChange: (options: SuperintendentFilterOption[]) => void;
	selected: SuperintendentFilterOption[];
	forBulkModal?: boolean;
	onClear?: () => void;
	autoOpen?: boolean;
	fieldReportFullViewFilterContext: FieldReportFullViewFilterContextType;
}

type Props = OwnProps & ConnectedProps<typeof connector>;

const SuperintendentFilter: React.FC<Props> = (props) => {
	const {
		onChange,
		findAllForFieldReportFilter,
		selected,
		forBulkModal,
		onClear,
		autoOpen,
		fieldReportFullViewFilterContext,
	} = props;

	const [options, setOptions] = React.useState<SuperintendentFilterOption[] | null>(null);

	const fetchOptions = React.useCallback(async () => {
		const _options = await findAllForFieldReportFilter();
		const formattedOptions = _options.superintendents
			.map((option) => ({ id: option.id, label: `${option.fullName} (${option.uniqueId})` }))
			.sort((_a, _b) => {
				if (_a.label < _b.label) {
					return -1;
				}
				if (_a.label > _b.label) {
					return 1;
				}
				return 0;
			});
		setOptions(formattedOptions);
		fieldReportFullViewFilterContext.setSuperintendentOptions(formattedOptions);
	}, [fieldReportFullViewFilterContext, findAllForFieldReportFilter]);

	React.useEffect(() => {
		if (!fieldReportFullViewFilterContext.superintendentOptions.length) {
			fetchOptions();
		} else {
			setOptions(fieldReportFullViewFilterContext.superintendentOptions);
		}
	}, [fetchOptions, fieldReportFullViewFilterContext.superintendentOptions, findAllForFieldReportFilter]);

	const onValueChange = React.useCallback((_options: SuperintendentFilterOption[]) => {
		onChange(_options);
	}, [onChange]);

	return (
		<AdditionalFilter
			autoOpen={!!autoOpen}
			filterName="Superintendent"
			forBulkModal={!!forBulkModal}
			onChange={onValueChange}
			onRemoveFilter={onClear}
			options={options}
			selected={selected}
		/>
	);
};

function mapDispatchToProps() {
	return {
		findAllForFieldReportFilter: AccountActions.findAllSuperintendentsForFieldReportFilter,
	};
}

const connector = connect(null, mapDispatchToProps());

const enhance = compose<React.ComponentType<OwnProps>>(
	React.memo,
	connector
);

export default enhance(SuperintendentFilter);
